import React, { useEffect, useState } from 'react';
import './WorldpayPaymentPanel.css';
import {
  CancellationPayment,
  fetchOrCreateWorldpayReference,
  confirmWorldpayPayment,
} from '../api/payments';

const currencyFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

interface WorldpayPaymentPanelProps {
  payment: CancellationPayment;
  onClose: () => void;
  onSettled: () => void;
}

/**
 * "Take card payment by phone (Worldpay)" panel (design doc 2026-08-20).
 * Card data never touches our systems: staff key it into Worldpay's own
 * Virtual Terminal page in a new tab. This panel only shows the amount
 * (inc VAT, taken from the STORED record -- never recomputed, per the
 * spec's explicit warning about the ex-VAT double-charge trap elsewhere in
 * the codebase) and the reference. PCP verifies the transaction with
 * Worldpay before recording any money; this is not a staff override.
 */
export const WorldpayPaymentPanel: React.FC<WorldpayPaymentPanelProps> = ({
  payment,
  onClose,
  onSettled,
}) => {
  // A reference is minted per charge attempt. The payment prop is merely a
  // cached list snapshot, so it can contain a reference a webhook consumed
  // between loading the list and opening this panel. Only the fresh backend
  // response is safe to display and submit.
  const [reference, setReference] = useState<string | null>(null);
  const [vtUrl, setVtUrl] = useState<string>('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const previouslyPaid = payment.manualPayment?.amountPaid || 0;
  const outstanding = Math.max(0, (payment.amount || 0) - previouslyPaid);

  const [confirmAmount, setConfirmAmount] = useState(outstanding > 0 ? outstanding.toFixed(2) : '');
  const [confirmIsFinal, setConfirmIsFinal] = useState(true);
  const [confirmNotes, setConfirmNotes] = useState('');
  const [transactionReference, setTransactionReference] = useState('');
  const [overpayAcknowledged, setOverpayAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reconciliationNotice, setReconciliationNotice] = useState<string | null>(null);
  const [followUpPending, setFollowUpPending] = useState(false);

  useEffect(() => {
    // The backend returns the existing unsettled reference or mints a new
    // one if the cached value was consumed. Fetching on every panel open is
    // therefore safe and is the only authoritative source for this attempt.
    let cancelled = false;
    setReference(null);
    setTransactionReference('');
    setVtUrl('');
    setLoadError(null);
    setFollowUpPending(false);
    setLoading(true);
    fetchOrCreateWorldpayReference(payment.id)
      .then((data) => {
        if (cancelled) return;
        if (!data.worldpayReference || data.worldpayReference.length < 9) {
          setLoadError('The payment reference is too short for Worldpay. Close and reopen this window to refresh it. If this continues, contact support before taking payment.');
          return;
        }
        setReference(data.worldpayReference);
        setVtUrl(data.virtualTerminalUrl);
      })
      .catch((err: any) => {
        if (cancelled) return;
        if (err?.response?.data?.code === 'worldpay_follow_up_pending' && err.response.data.paymentRecorded === true) {
          setFollowUpPending(true);
        }
        setReference(null);
        setVtUrl('');
        setLoadError(
          err?.response?.data?.error ||
          err?.message ||
          'Could not generate the Worldpay reference'
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [payment.id]);

  const handleCopy = () => {
    if (!reference) return;
    navigator.clipboard.writeText(reference);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirm = async () => {
    const amountPaid = parseFloat(confirmAmount);
    if (!reference || !Number.isFinite(amountPaid) || amountPaid <= 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const response = await confirmWorldpayPayment(payment.id, {
        amountPaid,
        isFinal: confirmIsFinal,
        worldpayReference: reference,
        ...(transactionReference.trim() ? { worldpayTransactionReference: transactionReference.trim() } : {}),
        notes: confirmNotes.trim() || undefined,
      });
      if (response.reconciliationFlag) {
        // Final fix-wave C2: money was taken and recorded, but the amount
        // exceeds what's owed, so the backend deliberately did NOT
        // auto-complete or receipt it -- a human has to resolve it via the
        // needs-reconciliation queue. This must not read as a routine
        // success, so it holds the panel open on a distinct notice instead
        // of closing straight into onSettled() like a normal confirmation.
        setReconciliationNotice(
          response.message ||
          'Payment recorded, but the amount exceeds what is owed — flagged for reconciliation rather than auto-completed.'
        );
      } else {
        onSettled();
      }
    } catch (err: any) {
      if (err?.response?.data?.code === 'worldpay_follow_up_pending' && err.response.data.paymentRecorded === true) {
        setFollowUpPending(true);
      }
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to record the Worldpay payment');
    } finally {
      setSubmitting(false);
    }
  };

  const amountValue = confirmAmount !== '' ? parseFloat(confirmAmount) : NaN;
  // The £2160-for-£216 keying slip: warn just as hard for TOO MUCH as the
  // existing warning does for too little. The backend now accepts an
  // overpayment (flagged for reconciliation, never auto-completed -- see
  // C2 above) rather than rejecting it outright, so this can't be a hard
  // block; it must stop an accidental submission without blocking a
  // deliberate one (e.g. correcting a previous shortfall).
  const isOverpay = Number.isFinite(amountValue) && outstanding > 0 && amountValue > outstanding + 0.005;

  if (followUpPending) {
    return (
      <div className="void-modal-content worldpay-panel">
        <h3>Payment recorded — follow-up processing pending</h3>
        <div role="alert">
          <p>The payment is saved in PCP, but updating the linked records has not finished.</p>
          <p>Do not take or record this payment again. Contact support to finish processing before taking another payment on this record.</p>
        </div>
        <div className="worldpay-panel__actions">
          <button type="button" className="worldpay-panel__button worldpay-panel__button--primary" onClick={onSettled}>
            Close and refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="void-modal-content worldpay-panel">
      <h3>Take card payment by phone (Worldpay)</h3>
      <p className="worldpay-panel__intro">
        Card details are entered directly into Worldpay's Virtual Terminal — they never touch our systems.
      </p>

      {loadError && (
        <div className="admin-payments-feedback error" style={{ marginBottom: '12px' }} role="alert">
          {loadError}
        </div>
      )}

      <section className="worldpay-panel__terminal" aria-labelledby="worldpay-terminal-step">
      <h4 id="worldpay-terminal-step" className="worldpay-panel__step"><span aria-hidden="true">1</span> Take payment in Worldpay</h4>
      <div className="worldpay-panel__details">
      <div className="worldpay-panel__detail">
        <span className="worldpay-panel__label">Amount to take (inc VAT):</span>
        <strong className="worldpay-panel__amount">{currencyFormatter.format(outstanding)}</strong>
      </div>

      <div className="worldpay-panel__detail">
        <span className="worldpay-panel__label">Reference:</span>
        {loading ? (
          <span>Generating…</span>
        ) : (
          reference && (
            <span className="worldpay-panel__reference">
              <code>{reference}</code>
              <button type="button" onClick={handleCopy} className="worldpay-panel__button worldpay-panel__button--copy">
                {copied ? 'Copied' : 'Copy'}
              </button>
            </span>
          )
        )}
      </div>
      </div>

      {vtUrl && (
        <div>
          <a className="worldpay-panel__button worldpay-panel__button--primary worldpay-panel__terminal-link" href={vtUrl} target="_blank" rel="noopener noreferrer">
            Open the Worldpay Virtual Terminal
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      )}

      <p className="worldpay-panel__hint">
        Enter this amount and this reference into the Virtual Terminal, then take the client's card details there.
      </p>
      </section>
      <section className="worldpay-panel__confirmation" aria-labelledby="worldpay-confirm-step">
      <h4 id="worldpay-confirm-step" className="worldpay-panel__step"><span aria-hidden="true">2</span> Verify payment with Worldpay</h4>
      <p className="worldpay-panel__hint">Enter the amount taken in Worldpay. PCP will verify the matching successful transaction before recording the payment. This button does not charge the card.</p>

      {reconciliationNotice ? (
        // Distinct from a plain success: the money is recorded, but the
        // backend refused to auto-complete or receipt it, so a human still
        // has to resolve it (see the needs-reconciliation queue). Holding
        // the panel open on this notice, rather than closing straight back
        // to the list, is deliberate -- staff must see it, not have it
        // flash past.
        <div className="admin-payments-feedback warning" role="alert">
          <strong>Recorded — needs reconciliation</strong>
          <p style={{ margin: '8px 0' }}>{reconciliationNotice}</p>
          <button
            type="button"
            className="worldpay-panel__button worldpay-panel__button--primary"
            onClick={() => { setReconciliationNotice(null); onSettled(); }}
          >
            OK, got it
          </button>
        </div>
      ) : (
        <>
          {submitError && (
            <div className="admin-payments-feedback error" style={{ marginBottom: '12px' }} role="alert">
              {submitError}
            </div>
          )}

          <div className="worldpay-panel__field">
            <label htmlFor="worldpay-confirm-amount">Amount taken</label>
            <div className="worldpay-panel__money-input">
            <span aria-hidden="true">£</span>
            <input
              id="worldpay-confirm-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={confirmAmount}
              onChange={(e) => {
                setConfirmAmount(e.target.value);
                setOverpayAcknowledged(false);
              }}
            />
            </div>
          </div>

          <div className="worldpay-panel__field">
            <label htmlFor="worldpay-transaction-reference">Worldpay transaction reference (optional)</label>
            <input
              id="worldpay-transaction-reference"
              type="text"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', minHeight: '44px', font: 'inherit', fontSize: '14px' }}
              value={transactionReference}
              onChange={(e) => setTransactionReference(e.target.value)}
              disabled={submitting}
              aria-describedby="worldpay-transaction-reference-help"
            />
            <p id="worldpay-transaction-reference-help" className="worldpay-panel__hint">
              If Worldpay shows a different transaction reference, paste it here. Check the receipt belongs to this client and payment.
            </p>
          </div>

          <fieldset className="worldpay-panel__payment-type">
            <legend>Payment type</legend>
            <label className={confirmIsFinal ? 'worldpay-panel__option is-selected' : 'worldpay-panel__option'}>
              <input type="radio" name="worldpay-payment-type" checked={confirmIsFinal} onChange={() => setConfirmIsFinal(true)} /> Final payment
            </label>
            <label className={!confirmIsFinal ? 'worldpay-panel__option is-selected' : 'worldpay-panel__option'}>
              <input type="radio" name="worldpay-payment-type" checked={!confirmIsFinal} onChange={() => setConfirmIsFinal(false)} /> Partial payment
            </label>
          </fieldset>

          {confirmIsFinal && confirmAmount !== '' && parseFloat(confirmAmount) < outstanding && (
            <p style={{ color: '#b45309', fontSize: '13px', marginTop: '4px' }}>
              This is less than the outstanding balance — did you mean to select "Partial payment"?
            </p>
          )}

          {isOverpay && (
            <div className="admin-payments-feedback warning" style={{ marginTop: '8px' }} role="alert">
              <p style={{ margin: 0 }}>
                This is MORE than the outstanding balance ({currencyFormatter.format(outstanding)}) — check you
                haven't mistyped the amount (e.g. an extra digit).
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  checked={overpayAcknowledged}
                  onChange={(e) => setOverpayAcknowledged(e.target.checked)}
                />
                I've checked this figure is correct — take this amount anyway
              </label>
            </div>
          )}

          <div className="worldpay-panel__field">
            <label htmlFor="worldpay-confirm-notes">Notes (optional)</label>
            <textarea
              id="worldpay-confirm-notes"
              value={confirmNotes}
              onChange={(e) => setConfirmNotes(e.target.value)}
              rows={2}
            />
          </div>

          <div className="worldpay-panel__actions">
            <button type="button" className="worldpay-panel__button worldpay-panel__button--secondary" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="worldpay-panel__button worldpay-panel__button--primary"
              onClick={handleConfirm}
              disabled={
                submitting ||
                loading ||
                !reference ||
                !confirmAmount ||
                parseFloat(confirmAmount) <= 0 ||
                (isOverpay && !overpayAcknowledged)
              }
            >
              {submitting ? 'Checking Worldpay…' : 'Check Worldpay payment'}
            </button>
          </div>
        </>
      )}
      </section>
    </div>
  );
};

export default WorldpayPaymentPanel;
